import moment from "moment";

// Hour math for Time Management.
//
// The timesheet endpoints return each bucket as an "HH:MM" string, or null when
// nothing was logged for that category. Every total on this page is accumulated
// in whole minutes and only formatted back to "HH:MM" at render time. Summing
// the strings, or going via floating-point hours, drifts once enough rows land
// on the same technician.
//
// Totals routinely exceed 24 hours (a two-week cycle is ~80), so the formatter
// never wraps at 24 — "142:30" is a valid, expected output.

// "HH:MM" -> minutes. Anything unparseable counts as zero: a missing bucket and
// a malformed one both mean "no hours we can trust", and a NaN would poison
// every total downstream of it.
export const parseHhMm = (value) => {
  if (typeof value !== "string") return 0;
  const match = value.trim().match(/^(\d+):([0-5]\d)$/);
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
};

// minutes -> "HH:MM", zero-padded on both sides. Hours are not capped at 24.
export const formatHhMm = (minutes) => {
  const safe = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0;
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

// minutes -> decimal hours, to 2dp. Payroll systems take decimal, not HH:MM,
// so the CSV export and the per-row tooltip both use this.
export const minutesToDecimal = (minutes) => {
  const safe = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  return Math.round((safe / 60) * 100) / 100;
};

// The three buckets every timesheet row carries, in the order they are shown.
export const HOUR_BUCKETS = [
  { key: "regularTime", label: "RT", title: "Regular" },
  { key: "overTime", label: "OT", title: "Overtime" },
  { key: "travelTime", label: "TT", title: "Travel" },
];

// Zeroed accumulator, shaped like the totals every consumer expects.
// doubleMinutes is only ever populated from the server's derived figures.
// There is no entered double-time bucket on a timesheet — it is a premium the
// engine works out, never something anyone typed — so summing raw rows leaves
// it at zero by design.
const emptyTotals = () => ({
  regularMinutes: 0,
  overMinutes: 0,
  doubleMinutes: 0,
  travelMinutes: 0,
  totalMinutes: 0,
});

// The overtime rules a user can be assigned, mirroring OtRule on the backend.
// A technician with neither has not been classified by payroll yet.
//
// Both the assignment drawer and the hours table label these, so the strings
// live here rather than in either one — a technician's rule reading "8/day" in
// one place and something else in the other is the kind of drift that costs
// trust in a payroll screen.
export const OT_RULE_DAILY_8 = "daily_8";
export const OT_RULE_WEEKLY_40 = "weekly_40";

// The two overtime rules are held as their own flags now, the same way the
// double-time premiums are. They stopped being alternatives: a person can be
// paid past 8 hours in a day *and* past 40 in a week, and a week holding both
// earns the greater of the two rather than the sum.
export const OT_DAILY_8 = "otDaily8";
export const OT_WEEKLY_40 = "otWeekly40";

/** Compact form, for a chip sitting beside a name. */
export const OT_RULE_SHORT = {
  [OT_RULE_DAILY_8]: "OT over 8 hrs/day",
  [OT_RULE_WEEKLY_40]: "OT over 40 hrs/wk",
};

/** Full form, for the picker and for explanatory copy. */
export const OT_RULE_LABELS = {
  [OT_RULE_DAILY_8]: "Over 8 hrs/day",
  [OT_RULE_WEEKLY_40]: "Over 40 hrs/wk",
};

// The two double-time premiums, which layer on top of the overtime rule rather
// than replacing it.
export const DT_OVER_12 = "dtOver12";
export const DT_SEVENTH_DAY = "dtSeventhDay";

export const DT_RULE_LABELS = {
  [DT_OVER_12]: "Double time over 12 hrs/day",
  [DT_SEVENTH_DAY]: "Double time on a 7th consecutive day",
};

/** Compact form, for chips beside a name. */
export const DT_RULE_SHORT = {
  [DT_OVER_12]: "DT over 12/day",
  [DT_SEVENTH_DAY]: "DT on 7th day",
};

/**
 * Every pay rule a technician can be assigned, as one catalog.
 *
 * The assignment UI is generated from this rather than hand-written per rule,
 * so adding the next one is an entry here plus the column and engine support it
 * needs — not another control to lay out, another save path, and another place
 * for a label to drift out of step with the engine.
 *
 * Groups differ in how many can be held at once, which is a property of the
 * policy rather than of the widget:
 *
 * - **overtime** is `one`. Two overtime rules on one person is not a policy, it
 *   is a contradiction — nothing could decide which threshold to measure at.
 * - **doubleTime** is `many`. The premiums layer on top of the overtime rule and
 *   on each other, so every combination is legitimate.
 *
 * Each option names the field it writes and the value that means "on", so both
 * kinds are read and written through the same two helpers below.
 *
 * Declared after the label maps it reads: this is evaluated at module load, and
 * referencing a const declared further down would throw before anything renders.
 */
export const PAY_RULE_GROUPS = [
  {
    key: "overtime",
    label: "Overtime",
    hint: "Any that apply",
    select: "many",
    options: [
      {
        id: OT_DAILY_8,
        field: OT_DAILY_8,
        value: true,
        label: OT_RULE_LABELS[OT_RULE_DAILY_8],
        chip: "8/day",
      },
      {
        id: OT_WEEKLY_40,
        field: OT_WEEKLY_40,
        value: true,
        label: OT_RULE_LABELS[OT_RULE_WEEKLY_40],
        chip: "40/wk",
      },
    ],
  },
  {
    key: "doubleTime",
    label: "Double time",
    hint: "Any that apply",
    select: "many",
    options: [
      {
        id: DT_OVER_12,
        field: DT_OVER_12,
        value: true,
        label: "Over 12 hrs/day",
        chip: "DT 12/day",
      },
      {
        id: DT_SEVENTH_DAY,
        field: DT_SEVENTH_DAY,
        value: true,
        label: "7th consecutive day worked",
        chip: "DT 7th day",
      },
    ],
  },
];

export const ALL_PAY_RULES = PAY_RULE_GROUPS.flatMap((group) => group.options);

/** The four flag fields, held on both a user and a branch. */
export const PAY_RULE_FIELDS = [OT_DAILY_8, OT_WEEKLY_40, DT_OVER_12, DT_SEVENTH_DAY];

/** Whether a person has any assignment of their own, as opposed to following the branch. */
export const hasOwnPayRule = (user) => PAY_RULE_FIELDS.some((field) => (user?.[field] ?? null) !== null);

/**
 * The rules a person is actually measured under: their own flag where one is
 * set, otherwise the first of the given layers that sets it — the branch's
 * rules for the roles they hold, then the branch's default — otherwise
 * nothing. Per flag, the same way the server resolves it, so the drawer shows
 * what the engine will use.
 */
export const effectivePayRules = (user, ...layers) => {
  const out = {};
  for (const field of PAY_RULE_FIELDS) {
    const mine = user?.[field] ?? null;
    if (mine !== null) {
      out[field] = mine;
      continue;
    }
    const layer = layers.find((item) => (item?.[field] ?? null) !== null);
    out[field] = layer ? layer[field] : null;
  }
  return out;
};

/** Whether one catalog option is currently assigned to a user. */
export const isRuleOn = (user, option) => (user?.[option.field] ?? null) === option.value;

/** The catalog options a user currently holds, in catalog order. */
export const assignedRules = (user) => ALL_PAY_RULES.filter((option) => isRuleOn(user, option));

/**
 * Whether payroll has classified this person for overtime at all.
 *
 * Holding neither overtime rule is the unassigned state — the engine declines
 * to compute rather than guess. The double-time premiums are not part of this
 * test: they are additions to a rule, and one on its own would still leave
 * nothing to derive.
 */
const weekKeyOf = (date, cycle) => startOfCycleWeek(date, cycle).format("YYYY-MM-DD");

/**
 * Where the two calendars disagree about a technician's hours.
 *
 * Working branches on both cycles is not itself a problem. What matters is
 * whether the calendars would file those hours differently: two days that sit
 * in one week under Sun-Sat and in two weeks under Mon-Sun, or the reverse.
 * When both agree, the hours land together whichever cycle measures them and
 * there is nothing for anyone to decide.
 *
 * The two only ever part company around a Sunday — it opens a Sun-Sat week and
 * closes a Mon-Sun one — so a pair of days is compared rather than a date range
 * guessed at. Hours on branches of both cycles in, say, the same Tuesday raise
 * no question at all, and flagging them would spend the warning on a
 * non-event.
 *
 * Returns the rows caught up in a disagreement, or an empty array.
 */
export const crossoverConflicts = (tech) => {
  const byCycle = new Map();
  for (const row of tech?.rows ?? []) {
    if (!row.date || row.branchCycle == null) continue;
    const cycle = row.branchCycle === 1 ? 1 : 0;
    if (!byCycle.has(cycle)) byCycle.set(cycle, new Map());
    // One entry per date per cycle; several work orders on one day tell the
    // same story once.
    if (!byCycle.get(cycle).has(row.date)) {
      byCycle.get(cycle).set(row.date, { date: row.date, cycle, branch: row.branch || "" });
    }
  }
  if (byCycle.size < 2) return [];

  const sunSat = [...(byCycle.get(0)?.values() ?? [])];
  const monSun = [...(byCycle.get(1)?.values() ?? [])];
  const involved = new Map();

  for (const a of sunSat) {
    for (const b of monSun) {
      const togetherOnSunSat = weekKeyOf(a.date, 0) === weekKeyOf(b.date, 0);
      const togetherOnMonSun = weekKeyOf(a.date, 1) === weekKeyOf(b.date, 1);
      if (togetherOnSunSat === togetherOnMonSun) continue;
      for (const entry of [a, b]) {
        involved.set(`${entry.cycle}|${entry.date}`, entry);
      }
    }
  }

  return [...involved.values()].sort(
    (x, y) => x.date.localeCompare(y.date) || x.cycle - y.cycle,
  );
};

/**
 * The weeks in which this technician worked branches on both calendars.
 *
 * Named on their own cycle, which is how a split decision is recorded. This is
 * a wider test than crossoverConflicts on purpose: splitting is available
 * wherever a week holds hours from both calendars, whether or not the two would
 * file them differently. The conflict test decides what to *warn* about; this
 * decides what can be *acted on*, and tying the button to the warning left it
 * rendering on weeks it then refused to do anything with.
 */
export const crossoverWeekStarts = (tech) => {
  const cyclesByWeek = new Map();
  for (const row of tech?.rows ?? []) {
    if (!row.date || row.branchCycle == null) continue;
    const week = startOfCycleWeek(row.date, tech.weekStartsOn ?? 0).format("YYYY-MM-DD");
    if (!cyclesByWeek.has(week)) cyclesByWeek.set(week, new Set());
    cyclesByWeek.get(week).add(row.branchCycle === 1 ? 1 : 0);
  }
  return [...cyclesByWeek.entries()]
    .filter(([, cycles]) => cycles.size > 1)
    .map(([week]) => week)
    .sort();
};

/**
 * Whether the two calendars would file any of this technician's hours
 * differently.
 *
 * Accepts a whole technician or one of the halves produced by
 * splitByBranchCycle. A half carries the answer worked out from the whole,
 * because its own rows are all on one calendar and could never disagree with
 * themselves.
 */
export const isCrossover = (tech) =>
  Array.isArray(tech?.crossoverConflicts)
    ? tech.crossoverConflicts.length > 0
    : crossoverConflicts(tech).length > 0;

/**
 * One display row per technician per week cycle they logged hours to.
 *
 * The grouping is by the cycle of the **branch the work order belongs to**, not
 * by the technician. Someone who worked both appears twice, which is the point:
 * the repetition is a louder signal that their week spans two calendars than
 * any badge on a single row would be.
 *
 * This is presentation only. Overtime is still derived per technician across
 * all of their hours on one cycle — bucketing the *computation* this way is
 * what loses overtime when neither bucket reaches 40 on its own. So a slice
 * carries the row-level figures the derivation already attributed to those
 * work orders, and `sliceOf` points back at the whole person.
 *
 * Row-level values use the derived split where the day could be placed on a
 * clock and the entered values otherwise — the same choice, made the same way,
 * as the drill-down and the exports.
 */
export const splitByBranchCycle = (technicians, splitWeekKeys = new Set()) => {
  // A decision is recorded per technician per week, so a person is measured
  // apart when any week of theirs in view has been split. Ranges wider than a
  // week are rare here and a mixed answer would be unreadable — the row can
  // only show one set of figures.
  const decidedApart = (tech) => {
    if (tech.technicianId == null || splitWeekKeys.size === 0) return false;
    for (const row of tech.rows) {
      if (!row.date) continue;
      const week = startOfCycleWeek(row.date, tech.weekStartsOn ?? 0).format("YYYY-MM-DD");
      if (splitWeekKeys.has(`${tech.technicianId}|${week}`)) return true;
    }
    return false;
  };

  const out = [];
  for (const tech of technicians) {
    const byCycle = new Map();
    for (const row of tech.rows) {
      const cycle = row.branchCycle === 1 ? 1 : 0;
      if (!byCycle.has(cycle)) byCycle.set(cycle, []);
      byCycle.get(cycle).push(row);
    }

    // No rows to place — detail was capped away. Keep the person visible under
    // the cycle their overtime was measured on rather than dropping them.
    if (byCycle.size === 0) {
      out.push({
        ...tech,
        cycle: tech.weekStartsOn ?? 0,
        crossoverWeekStarts: [],
        crossoverConflicts: [],
        sliceOf: null,
      });
      continue;
    }

    const split = byCycle.size > 1;
    // When payroll has chosen to split, each half is shown as the engine
    // measured it against its own branch's calendar, rather than as its share
    // of one calculation across the week. The two answers differ exactly where
    // a threshold falls in one grouping and not the other, which is the whole
    // reason the choice exists.
    const measuredApart = split && decidedApart(tech) && Array.isArray(tech.crossoverSplit);

    // Computed from the whole technician and carried onto each half.
    //
    // A slice holds one calendar's rows by definition, so asking it "did this
    // person work both calendars?" is always no. Every question about the
    // crossover has to be answered before the split, not after it — asking it
    // of a half is how the button came to report nothing to split on a week
    // that plainly had something to split.
    const weekStarts = crossoverWeekStarts(tech);
    const conflicts = crossoverConflicts(tech);

    for (const [cycle, rows] of [...byCycle.entries()].sort(([a], [b]) => a - b)) {
      const apart = measuredApart
        ? tech.crossoverSplit.find((part) => part.branchCycle === cycle)
        : null;
      const totals = rows.reduce(
        (sum, row) => {
          sum.regularMinutes += parseHhMm(row.derivedRegularTime ?? row.regularTime);
          sum.overMinutes += parseHhMm(row.derivedOverTime ?? row.overTime);
          sum.doubleMinutes += parseHhMm(row.derivedDoubleTime);
          // Derived TT is the travel that was paid at the regular rate; RT
          // excludes it, so the four columns add up rather than counting
          // travel under two of them. An unattributed row keeps its entered
          // travel, and its entered regular never included travel to begin
          // with.
          sum.travelMinutes += parseHhMm(row.derivedTravelTime ?? row.travelTime);
          return sum;
        },
        { regularMinutes: 0, overMinutes: 0, doubleMinutes: 0, travelMinutes: 0 },
      );
      out.push({
        ...tech,
        rows,
        cycle,
        crossoverWeekStarts: weekStarts,
        crossoverConflicts: conflicts,
        ...totals,
        ...(apart?.derived
          ? {
              regularMinutes: apart.derived.regularMinutes - (apart.derived.travelRegularMinutes ?? 0),
              overMinutes: apart.derived.overtimeMinutes,
              doubleMinutes: apart.derived.doubleTimeMinutes,
              travelMinutes: apart.derived.travelRegularMinutes ?? apart.travelMinutes ?? totals.travelMinutes,
            }
          : {}),
        measuredApart: Boolean(apart),
        // RT has had its travel taken out and TT is that travel, so the paid
        // total is the four columns added. (Travel still counted toward the
        // threshold when the split was computed — this is about what each
        // column shows, not about what earned overtime.)
        totalMinutes: apart?.derived
          ? apart.derived.regularMinutes + apart.derived.overtimeMinutes + apart.derived.doubleTimeMinutes
          : totals.regularMinutes + totals.overMinutes + totals.doubleMinutes + totals.travelMinutes,
        // What the whole person earned, so a slice can say what it is a slice
        // of rather than being mistaken for the week entire.
        sliceOf: split
          ? {
              regularMinutes: tech.regularMinutes,
              overMinutes: tech.overMinutes,
              doubleMinutes: tech.doubleMinutes ?? 0,
              travelMinutes: tech.travelMinutes,
              totalMinutes: tech.totalMinutes,
              measuredOn: tech.weekStartsOn ?? 0,
            }
          : null,
      });
    }
  }
  return out;
};

/**
 * What the two readings come to for one crossover technician.
 *
 * Returned so the choice can be offered with its consequence attached rather
 * than as a bare toggle. Splitting is a legitimate reading and it is also the
 * one that can lose overtime — when a week is measured in two halves, neither
 * half may reach 40 even though the week did. Showing both figures at the
 * moment of choosing is the difference between a decision and a guess.
 */
export const crossoverOutcomes = (tech) => {
  if (!Array.isArray(tech?.crossoverSplit)) return null;
  const apart = tech.crossoverSplit.reduce(
    (sum, part) => {
      sum.regularMinutes +=
        (part.derived?.regularMinutes ?? 0) - (part.derived?.travelRegularMinutes ?? 0);
      sum.overMinutes += part.derived?.overtimeMinutes ?? 0;
      sum.doubleMinutes += part.derived?.doubleTimeMinutes ?? 0;
      return sum;
    },
    { regularMinutes: 0, overMinutes: 0, doubleMinutes: 0 },
  );
  // The server's own combined figure, not this row's. A slice holds one
  // calendar's hours, and once a split is in force the technician's totals are
  // the split ones — so reading either back would put the chosen answer beside
  // itself and show no difference at all.
  const whole = tech.crossoverTogether?.derived;
  return {
    together: {
      regularMinutes: (whole?.regularMinutes ?? 0) - (whole?.travelRegularMinutes ?? 0),
      overMinutes: whole?.overtimeMinutes ?? 0,
      doubleMinutes: whole?.doubleTimeMinutes ?? 0,
    },
    apart,
  };
};

export const hasOvertimeRule = (user) =>
  isRuleOn(user, { field: OT_DAILY_8, value: true }) ||
  isRuleOn(user, { field: OT_WEEKLY_40, value: true }) ||
  // A server that predates the split still answers with the single column.
  (user?.[OT_DAILY_8] == null && user?.[OT_WEEKLY_40] == null && !!user?.otRule);

/** Short labels for the overtime rules a technician holds, in catalog order. */
export const overtimeRuleChips = (user) =>
  PAY_RULE_GROUPS[0].options.filter((option) => isRuleOn(user, option)).map((option) => option.chip);

// Add one preview row's buckets into an accumulator, in place.
const addRow = (totals, row) => {
  const regular = parseHhMm(row.regularTime);
  const over = parseHhMm(row.overTime);
  const travel = parseHhMm(row.travelTime);
  totals.regularMinutes += regular;
  totals.overMinutes += over;
  totals.travelMinutes += travel;
  totals.totalMinutes += regular + over + travel;
  return totals;
};

/**
 * Group flat preview rows into one entry per technician.
 *
 * Rows arrive as technician x work order x day, which is the grain payroll
 * needs to drill into but not the grain they review at. Grouping is keyed on
 * the technician's *name* because that is the only identifier
 * hours_worked_report_preview returns — there is no userId in the row. Two
 * technicians who genuinely share a name would merge here; that is a real
 * limitation of the endpoint, not something the client can resolve, and it is
 * called out in the page's data-source note.
 *
 * Returns technicians sorted by name, each with their own rows kept for the
 * drill-down (sorted by date, then WO).
 */
export const groupByTechnician = (rows) => {
  const byName = new Map();

  for (const row of rows) {
    const name = (row.technician || "").trim() || "Unassigned";
    if (!byName.has(name)) {
      byName.set(name, {
        technician: name,
        // Carried on the entry, not just on its rows: the split decision is
        // recorded against a user id, and grouping by display name alone
        // leaves nothing to record it against.
        technicianId: null,
        rows: [],
        // The cycle this person's overtime is measured on, from their own
        // branch. Constant across their rows — the server resolves it per
        // technician, not per work order.
        weekStartsOn: undefined,
        // The cycles of the branches they actually logged hours to, which is a
        // different question. More than one is a crossover.
        branchCycles: new Set(),
        otRule: undefined,
        [OT_DAILY_8]: undefined,
        [OT_WEEKLY_40]: undefined,
        [DT_OVER_12]: undefined,
        [DT_SEVENTH_DAY]: undefined,
        ...emptyTotals(),
      });
    }
    const entry = byName.get(name);
    entry.rows.push(row);
    addRow(entry, row);

    // Which overtime rule this person is paid under, carried on every row by
    // the report endpoint. Taken as "missing" if *any* row lacks it: because
    // grouping is keyed on name, two same-named technicians land in one entry,
    // and if either is unclassified the group is not safe to compute.
    if (entry.technicianId == null && row.technicianId != null) entry.technicianId = row.technicianId;
    if (entry.weekStartsOn === undefined && row.weekStartsOn != null) {
      entry.weekStartsOn = row.weekStartsOn === 1 ? 1 : 0;
    }
    if (row.branchCycle != null) entry.branchCycles.add(row.branchCycle === 1 ? 1 : 0);

    const rule = row.otRule ?? null;
    entry.otRule = entry.otRule === undefined ? rule : entry.otRule === rule ? rule : null;

    // The double-time premiums, read off the rows for the same reason and with
    // the same caution: a name collision means one entry holds two people, and
    // a premium only one of them carries must not be attributed to both.
    for (const key of [OT_DAILY_8, OT_WEEKLY_40, DT_OVER_12, DT_SEVENTH_DAY]) {
      const held = row[key] === true;
      entry[key] = entry[key] === undefined ? held : entry[key] && held;
    }
  }

  const technicians = [...byName.values()];
  for (const entry of technicians) {
    entry.rows.sort(
      (a, b) =>
        String(a.date || "").localeCompare(String(b.date || "")) ||
        String(a.woNumber || "").localeCompare(String(b.woNumber || ""))
    );
  }
  technicians.sort((a, b) => a.technician.localeCompare(b.technician));
  return technicians;
};

// Grand totals across every row, for the summary tiles and the totals row.
export const totalsFor = (rows) => rows.reduce(addRow, emptyTotals());

/**
 * Fold the backend's derived overtime onto the grouped technicians.
 *
 * The derived split replaces RT/OT *for display* only where a rule is
 * assigned; the entered values stay on the entry either way, because they are
 * what someone actually typed and are the audit trail. Where the two disagree,
 * both are kept so the row can say so rather than silently pick one.
 *
 * Matched on technicianId, falling back to name for rows that predate the id
 * being returned.
 */
export const applyDerivedOvertime = (technicians, otSummary = []) => {
  if (!Array.isArray(otSummary) || otSummary.length === 0) return technicians;

  const byId = new Map();
  const byName = new Map();
  for (const entry of otSummary) {
    if (entry.technicianId != null) byId.set(entry.technicianId, entry);
    if (entry.technician) byName.set(entry.technician, entry);
  }

  // Which summary entries were matched to a technician built from rows. What is
  // left over is someone whose detail rows fell past the response's cap: the
  // server derived their totals over the whole range, but none of their entries
  // came back. They are added below rather than dropped — a payroll screen that
  // silently omits a person is worse than one that shows their hours and says
  // the breakdown is missing.
  const matched = new Set();

  const withDerived = technicians.map((tech) => {
    const id = tech.rows.find((row) => row.technicianId != null)?.technicianId;
    const entry = (id != null ? byId.get(id) : undefined) ?? byName.get(tech.technician);
    if (!entry) return tech;
    matched.add(entry);

    const derived = entry.derived || null;
    return {
      ...tech,
      otRule: entry.otRule ?? tech.otRule ?? null,
      [OT_DAILY_8]: entry[OT_DAILY_8] ?? tech[OT_DAILY_8] ?? entry.otRule === OT_RULE_DAILY_8,
      [OT_WEEKLY_40]: entry[OT_WEEKLY_40] ?? tech[OT_WEEKLY_40] ?? entry.otRule === OT_RULE_WEEKLY_40,
      [DT_OVER_12]: entry[DT_OVER_12] ?? tech[DT_OVER_12] ?? false,
      [DT_SEVENTH_DAY]: entry[DT_SEVENTH_DAY] ?? tech[DT_SEVENTH_DAY] ?? false,
      partialWeeks: entry.partialWeeks || 0,
      // The same hours measured per branch calendar instead of together, for
      // anyone who worked both. Present only for them; the toggle that uses it
      // has nothing to offer otherwise.
      crossoverSplit: entry.crossoverSplit ?? null,
      // The combined reading, sent alongside. otSummary already reflects
      // whichever decision is stored, so it cannot double as the alternative —
      // read back it would show the chosen answer under both labels.
      crossoverTogether: entry.crossoverTogether ?? null,
      // Days taken as entered rather than derived, because overtime was already
      // typed on them.
      preservedDays: entry.preservedDays || 0,
      // Kept so a row can show that the derived figure differs from what was
      // typed on the timesheets, rather than overwriting it without a trace.
      enteredOverMinutes: tech.overMinutes,
      derived,
      // RT without the travel that sat in the regular band. Travel counts
      // toward the threshold, so the engine has it inside regularMinutes; it
      // also says how much, and that much is shown under TT instead.
      regularMinutes: derived
        ? derived.regularMinutes - (derived.travelRegularMinutes ?? 0)
        : tech.regularMinutes,
      overMinutes: derived ? derived.overtimeMinutes : tech.overMinutes,
      // Double time exists only as a derived figure. Without a rule assigned
      // there is nothing to fall back to, so it stays zero rather than
      // borrowing an entered value that never represented a premium.
      doubleMinutes: derived ? derived.doubleTimeMinutes : 0,
      travelMinutes: derived ? (derived.travelRegularMinutes ?? tech.travelMinutes) : tech.travelMinutes,
      // The paid total is what the engine derived across all three bands; RT
      // and TT are that regular band shown as two columns. Same number as
      // before — what changed is that the columns now add up to it.
      totalMinutes: derived
        ? derived.regularMinutes + derived.overtimeMinutes + derived.doubleTimeMinutes
        : tech.totalMinutes,
    };
  });

  const detailMissing = otSummary
    .filter((entry) => !matched.has(entry))
    .map((entry) => {
      const derived = entry.derived || null;
      return {
        technician: entry.technician || "Unassigned",
        technicianId: entry.technicianId ?? null,
        // No entries came back for this person, so there is nothing to drill
        // into. The flag is what lets the row say so instead of rendering an
        // empty breakdown that reads as "worked nothing".
        rows: [],
        detailTruncated: true,
        weekStartsOn: entry.weekStartsOn === 1 ? 1 : 0,
        // No rows came back, so nothing can be said about where they worked.
        branchCycles: new Set(),
        otRule: entry.otRule ?? null,
        [OT_DAILY_8]: entry[OT_DAILY_8] === true,
        [OT_WEEKLY_40]: entry[OT_WEEKLY_40] === true,
        [DT_OVER_12]: entry[DT_OVER_12] === true,
        [DT_SEVENTH_DAY]: entry[DT_SEVENTH_DAY] === true,
        partialWeeks: entry.partialWeeks || 0,
        preservedDays: entry.preservedDays || 0,
        enteredOverMinutes: entry.enteredOvertimeMinutes ?? 0,
        derived,
        regularMinutes: derived ? derived.regularMinutes - (derived.travelRegularMinutes ?? 0) : 0,
        overMinutes: derived ? derived.overtimeMinutes : 0,
        doubleMinutes: derived ? derived.doubleTimeMinutes : 0,
        travelMinutes: derived ? (derived.travelRegularMinutes ?? entry.travelMinutes ?? 0) : (entry.travelMinutes ?? 0),
        totalMinutes: derived
          ? derived.regularMinutes + derived.overtimeMinutes + derived.doubleTimeMinutes
          : 0,
      };
    });

  return detailMissing.length
    ? [...withDerived, ...detailMissing].sort((a, b) => a.technician.localeCompare(b.technician))
    : withDerived;
};

/** Grand totals across already-derived technician entries. */
export const totalsForTechnicians = (technicians) =>
  technicians.reduce((totals, tech) => {
    totals.regularMinutes += tech.regularMinutes;
    totals.overMinutes += tech.overMinutes;
    totals.doubleMinutes += tech.doubleMinutes ?? 0;
    totals.travelMinutes += tech.travelMinutes;
    totals.totalMinutes += tech.totalMinutes;
    return totals;
  }, emptyTotals());


// The two week cycles a branch can run, mirroring branch.timecardCycle.
// Branches genuinely differ: MRC Cypress and MRC Visalia run Monday-Sunday
// while the rest run Sunday-Saturday.
export const WEEK_CYCLE_SUN_SAT = 0;
export const WEEK_CYCLE_MON_SUN = 1;

// Both calendars at once. Not a cycle in its own right — no branch runs on it —
// but a way to look at people on either, which is the only way to see someone
// whose hours span the two.
export const WEEK_CYCLE_BOTH = "both";

export const isBothCycles = (cycle) => cycle === WEEK_CYCLE_BOTH;

/** The cycles a selection covers, always as a list. */
export const cyclesIn = (cycle) =>
  isBothCycles(cycle) ? [WEEK_CYCLE_SUN_SAT, WEEK_CYCLE_MON_SUN] : [cycle];

export const WEEK_CYCLE_OPTIONS = [
  { value: WEEK_CYCLE_SUN_SAT, label: "Sun \u2013 Sat" },
  { value: WEEK_CYCLE_MON_SUN, label: "Mon \u2013 Sun" },
  { value: WEEK_CYCLE_BOTH, label: "Both" },
];

/**
 * First day of the cycle week containing `date`.
 *
 * Deliberately the same arithmetic as weekKey() in the backend's
 * overtime.calc.ts: moment weeks start Sunday, so a Monday cycle is expressed
 * by shifting a day rather than by branching. If these two ever disagree, the
 * window a user selects would stop matching the week their overtime is
 * measured over, which is the whole problem this is here to avoid.
 */
export const startOfCycleWeek = (date, weekStartsOn) => {
  const day = moment(date);
  const shifted = weekStartsOn === WEEK_CYCLE_MON_SUN ? day.clone().subtract(1, "day") : day;
  return shifted.clone().startOf("week").add(weekStartsOn, "day").startOf("day");
};

/** [start, end] of the current cycle week. End is today, not the week's last day. */
export const thisCycleWeek = (weekStartsOn, today = new Date()) => {
  // Both cycles: start from whichever week began first, so neither calendar's
  // week is clipped. How wide that is depends on the day it is asked on — on a
  // Sunday the Sun-Sat week has just begun while the Mon-Sun week is closing,
  // so the union is a full seven days; on a Monday it is two.
  if (isBothCycles(weekStartsOn)) {
    const starts = cyclesIn(weekStartsOn).map((cycle) => startOfCycleWeek(today, cycle));
    return [moment.min(starts).toDate(), moment(today).endOf("day").toDate()];
  }
  return [startOfCycleWeek(today, weekStartsOn).toDate(), moment(today).endOf("day").toDate()];
};

/**
 * [start, end] of the previous, complete cycle week.
 *
 * Both cycles: the two complete weeks are offset by exactly one day, so their
 * union is always eight days — Sun-Sat's last week plus the Sunday that closes
 * Mon-Sun's.
 */
export const lastCycleWeek = (weekStartsOn, today = new Date()) => {
  const weeks = cyclesIn(weekStartsOn).map((cycle) => {
    const start = startOfCycleWeek(today, cycle).subtract(7, "days");
    return [start, start.clone().add(6, "days").endOf("day")];
  });
  return [
    moment.min(weeks.map(([start]) => start)).toDate(),
    moment.max(weeks.map(([, end]) => end)).toDate(),
  ];
};


/**
 * Trim a range that runs past today.
 *
 * Calendar periods do not care what today is: picking Q3 in August asks for
 * July-September, three quarters of which has not happened. Clamping keeps the
 * query honest (and cheaper) by asking only for days that could contain hours.
 *
 * A period entirely in the future is left alone — clamping it would produce a
 * backwards range, and showing "Oct 1 - Dec 31" with no rows is clearer than
 * showing something nonsensical.
 */
const clampToToday = ([start, end], today) => {
  const todayEnd = moment(today).endOf("day");
  if (moment(start).isAfter(todayEnd)) return [start, end];
  return [start, moment.min(moment(end), todayEnd).toDate()];
};

export const thisMonth = (today = new Date()) =>
  clampToToday([moment(today).startOf("month").toDate(), moment(today).endOf("month").toDate()], today);

export const lastMonth = (today = new Date()) => {
  const start = moment(today).subtract(1, "month").startOf("month");
  return [start.toDate(), start.clone().endOf("month").toDate()];
};

/**
 * A calendar quarter of the current year (1-4). Calendar, not fiscal — Makani
 * has no fiscal-year setting, so inventing one here would be a guess.
 */
export const calendarQuarter = (quarter, today = new Date()) => {
  const start = moment(today).startOf("year").add((quarter - 1) * 3, "months");
  return clampToToday([start.toDate(), start.clone().endOf("quarter").toDate()], today);
};

export const thisYear = (today = new Date()) =>
  clampToToday([moment(today).startOf("year").toDate(), moment(today).endOf("year").toDate()], today);

export const lastYear = (today = new Date()) => {
  const start = moment(today).subtract(1, "year").startOf("year");
  return [start.toDate(), start.clone().endOf("year").toDate()];
};


/**
 * A "HH:mm" clock time as "h:mm AM/PM". Stored 24-hour and displayed 12-hour,
 * matching the tech app that captures them — the display never changes what is
 * stored.
 */
export const formatClock = (value) => {
  if (typeof value !== "string") return null;
  const parsed = moment(value.trim(), "HH:mm", true);
  return parsed.isValid() ? parsed.format("h:mm A") : null;
};

/**
 * "7:00 AM – 3:30 PM" for a start/stop pair, or null when neither is present.
 *
 * Entries logged before the tech app captured clock times have no punches at
 * all, and will not gain any retroactively. A half-present pair renders the
 * side that exists rather than nothing, so a missing stop is visible as a
 * missing stop instead of looking like an entry with no punches.
 */
export const formatClockRange = (start, stop) => {
  const from = formatClock(start);
  const to = formatClock(stop);
  if (!from && !to) return null;
  return `${from || "—"} – ${to || "—"}`;
};


/** "HH:mm" -> minutes since midnight, or null when absent or unparseable. */
export const clockToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{1,2}):([0-5]\d)$/);
  if (!match) return null;
  const hours = Number(match[1]);
  if (hours > 23) return null;
  return hours * 60 + Number(match[2]);
};

/**
 * For each date, the index of the row the day's lunch is shown after.
 *
 * Chronological: the last span that had finished by the time the lunch
 * started, so a break between two stretches sits between them. A lunch taken
 * inside a span goes after that span; one before any punched span goes
 * against the first row. Never dropped: a day with no punches still shows
 * its lunch, against its first row.
 */
export const lunchAnchorsByDate = (rows) => {
  const byDate = new Map();
  rows.forEach((row, index) => {
    const key = row.date || "unknown";
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(index);
  });

  const anchors = new Map();
  for (const [date, indexes] of byDate) {
    const lunchStart = clockToMinutes(rows[indexes[0]].lunchStart);
    if (lunchStart === null) {
      // No span to place. If the technician said they took no lunch, or their
      // branch requires one and nothing was said, that is still worth a line —
      // under the day's first row, since there is no interrupted span for it
      // to sit against.
      if (rows[indexes[0]].noLunch || rows[indexes[0]].lunchRequired) {
        anchors.set(date, indexes[0]);
      }
      continue;
    }

    // Chronological: the lunch goes after the last span that had finished by
    // the time it started, so a break between two stretches sits between
    // them rather than after the one that began as it ended. A lunch taken
    // inside a span has only one place to go — after that span.
    let chosen = null;
    let chosenStop = -1;
    for (const index of indexes) {
      const start = clockToMinutes(rows[index].regularStart);
      const stop = clockToMinutes(rows[index].regularStop);
      if (start === null || stop === null) continue;
      // A span running past midnight ends on the following day's clock.
      const end = stop >= start ? stop : stop + 24 * 60;
      if (end <= lunchStart && end > chosenStop) {
        chosen = index;
        chosenStop = end;
      }
    }

    if (chosen === null) {
      for (const index of indexes) {
        const start = clockToMinutes(rows[index].regularStart);
        const stop = clockToMinutes(rows[index].regularStop);
        if (start === null || stop === null) continue;
        const end = stop >= start ? stop : stop + 24 * 60;
        const point = lunchStart >= start ? lunchStart : lunchStart + 24 * 60;
        if (point >= start && point < end) {
          chosen = index;
          break;
        }
      }
    }

    // Nothing had finished and nothing contains it: it comes before the day's
    // first punched span, and is shown against the first row.
    anchors.set(date, chosen ?? indexes[0]);
  }
  return anchors;
};

/**
 * The days a person was on call within a range, sorted, without repeats.
 *
 * On call is a fact about a day — not hours, not a span — and is never added to
 * any hours figure. This is only so the screen, the sheet and the CSV list the
 * same days.
 */
export const onCallDaysInRange = (days, range) => {
  const from = range?.[0] ? moment(range[0]).format("YYYY-MM-DD") : null;
  const to = range?.[1] ? moment(range[1]).format("YYYY-MM-DD") : null;
  const dates = new Set();
  for (const day of days || []) {
    const date = day?.date;
    if (!date) continue;
    if (from && date < from) continue;
    if (to && date > to) continue;
    dates.add(date);
  }
  return [...dates].sort();
};

/** "Fri Sep 5". */
export const describeOnCallDay = (date) => moment(date).format("ddd MMM D");

/** A stored YYYY-MM-DD day as the app writes dates on screen: 09/07/2026. */
export const displayDay = (date) => (date ? moment(date, "YYYY-MM-DD").format("MM/DD/YYYY") : "");


/**
 * The finished cycle weeks of one technician that the loaded range touches,
 * each with its sign-off state.
 *
 * `signoff` is the server's per-user record: their cycle, which week is this
 * week and last week on their own clock, and their signed weeks. Weeks are
 * the technician's own, not the selector's, so a Monday-to-Sunday person is
 * judged on their week whichever cycle the screen is showing. The current
 * week is included: it can be signed early, and is then closed like any other.
 */
export const signoffWeeksFor = (signoff, range) => {
  if (!signoff || !range?.[0] || !range?.[1]) return [];
  const cycle = signoff.cycle === 1 ? 1 : 0;
  const start = startOfCycleWeek(moment(range[0]).format("YYYY-MM-DD"), cycle);
  const end = moment(range[1]);
  const weeks = [];
  for (let cursor = start.clone(); !cursor.isAfter(end, "day"); cursor.add(7, "days")) {
    const weekStart = cursor.format("YYYY-MM-DD");
    // The week in progress can be signed early; a week not yet begun cannot.
    if (signoff.thisWeekStart && weekStart > signoff.thisWeekStart) break;
    const submission = (signoff.submissions || []).find((item) => item.weekStart === weekStart) || null;
    weeks.push({
      weekStart,
      weekEnd: cursor.clone().add(6, "days").format("YYYY-MM-DD"),
      status: submission?.status ?? "OPEN",
      submission,
      current: weekStart === signoff.thisWeekStart,
      // Still the technician's to sign from the app; afterwards only the
      // office can sign it for them.
      windowOpen: weekStart === signoff.lastWeekStart || weekStart === signoff.thisWeekStart,
    });
  }
  return weeks;
};


/**
 * The minute of the day a row's first punch lands on, for ordering a day's
 * entries the way they happened: the earlier of the work and travel starts.
 * Rows with no clock times at all go last, in the order they came, so an
 * entry typed as a duration by the office still has a place.
 */
export const firstPunchOf = (row) => {
  const starts = [clockToMinutes(row?.regularStart), clockToMinutes(row?.travelStart)].filter(
    (value) => value !== null
  );
  return starts.length ? Math.min(...starts) : Infinity;
};

/** Stable sort of `items` by the first punch of the row `rowOf(item)` names. */
export const byFirstPunch = (items, rowOf) =>
  items
    .map((item, position) => ({ item, position, at: firstPunchOf(rowOf(item)) }))
    .sort((a, b) => a.at - b.at || a.position - b.position)
    .map(({ item }) => item);
